import { Drawing, DrawingStyles, DrawingType } from '@editor/interfaces/drawing'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { cloneDeep, isEqual } from 'lodash'
import mixpanel from 'mixpanel-browser'

import { DEFAULT_DOCUMENT } from '../constants'
import { DEFAULT_STYLES } from '../definitions'

/**
 * Represents the state machine for the export workflow of drawings.
 *
 * The export process transitions through the following states in order:
 * 1. None:        No export is in progress.
 * 2. PrepCanvas:  Preparing the canvas and environment for export.
 * 3. ProcessData: Processing and gathering the data required for export.
 * 4. Rendering:   Rendering the drawings to the export format (e.g., image, PDF).
 * 5. Exporting:   Finalizing and saving/exporting the rendered output.
 *
 * Each state represents a distinct phase in the export workflow.
 */
export enum ExportState {
  /** No export is in progress. */
  None,
  /** Preparing the canvas and environment for export. */
  PrepCanvas,
  /** Processing and gathering the data required for export. */
  ProcessData,
  /** Rendering the drawings to the export format. */
  Rendering,
  /** Finalizing and saving/exporting the rendered output. */
  Exporting,
}

export interface DrawingsState {
  document: {
    drawings: Drawing[]
    editorElements: {
      inspectionItems: { id: string; order: number }[]
      polygons: { id: string; order: number }[]
      cylinders: { id: string; order: number }[]
    }
  }

  /**
   * The 2D equivalent of the document.
   */
  document2d: {
    drawings: Drawing[] | null
    mmToPxRatio: number | null
    scale: number
    exportState: ExportState
  }

  /**
   * UUIDs of drawings that should be skipped for rendering.
   * This is basically a shortcut of other UUID collections so it can
   * be centrally managed.
   */
  renderSkipUuids: string[]

  /**
   * Timestamp of the last modification.
   */
  lastModified: number

  /**
   * Default styles for the drawings.
   * This is used to determine the styles of the drawings when they are created.
   */
  defaultStyles: DrawingStyles

  /**
   * Styles that are being applied to the drawing currently being worked on.
   * Generally used by tool that does not add working drawing directly to drawings.
   * For those that does, the styles are applied directly to the drawing in the store.
   */
  workingStyles?: DrawingStyles
}

const initialState: DrawingsState = {
  document: cloneDeep(DEFAULT_DOCUMENT),
  document2d: { drawings: null, mmToPxRatio: null, scale: 1, exportState: ExportState.None },
  defaultStyles: cloneDeep(DEFAULT_STYLES),

  renderSkipUuids: [],

  lastModified: 0,
}

const drawingsSlice = createSlice({
  name: 'projects/blueprint-editor/drawings',
  initialState,
  reducers: {
    /**
     * Drawings
     */
    addDrawing: (state, { payload }: PayloadAction<Drawing>) => {
      state.document.drawings.push(payload)
      state.lastModified = Date.now()

      // Track drawing creation
      mixpanel.track('Drawing Created', {
        'Drawing Type': payload.drawing_type,
      })
    },
    setDrawings: (state, { payload }: PayloadAction<Drawing[]>) => {
      state.document.drawings = payload
    },
    removeDrawings: (state, { payload }: PayloadAction<string[]>) => {
      state.document.drawings = state.document.drawings.filter(
        ({ drawing_parameters: { uuid } }) => !payload.includes(uuid),
      )
      state.lastModified = Date.now()
    },
    resetDrawings: (state) => {
      state.document.drawings = []
    },

    undo: () => {},

    /**
     * Update a drawing's parameters by its UUID.
     */
    updateDrawingParameters: (state, { payload }: PayloadAction<Partial<Drawing['drawing_parameters']>>) => {
      const drawing = state.document.drawings.find((d) => d.drawing_parameters.uuid === payload.uuid)
      if (drawing) {
        const newParams = {
          ...drawing.drawing_parameters,
          ...payload,
        }

        if (isEqual(drawing.drawing_parameters, newParams)) return

        drawing.drawing_parameters = newParams
        state.lastModified = Date.now()
      }
    },

    /**
     * Update the styles of the drawings by their UUIDs.
     */
    updateDrawingStyles: (
      state,
      { payload: { uuids, styles } }: PayloadAction<{ uuids: string[]; styles: DrawingStyles }>,
    ) => {
      state.document.drawings.forEach((drawing) => {
        if (uuids.includes(drawing.drawing_parameters.uuid)) {
          // Use new width if provided, otherwise use the existing one.
          const newStrokeWidth =
            styles.strokeWidth !== undefined ? styles.strokeWidth : drawing.drawing_parameters.styles.strokeWidth

          drawing.drawing_parameters.styles = {
            ...drawing.drawing_parameters.styles,
            ...styles,
            // Polyline can't be 0 thickness, nothing will be rendered.
            strokeWidth:
              drawing.drawing_type === DrawingType.Polyline ? Math.max(newStrokeWidth || 1, 1) : newStrokeWidth,
          }
        }
      })
      state.lastModified = Date.now()
    },

    /**
     * Update the default styles for the drawings.
     */
    updateDefaultStyles: (state, { payload }: PayloadAction<DrawingStyles>) => {
      state.defaultStyles = {
        ...state.defaultStyles,
        ...payload,
      }
    },

    /**
     * Update the working styles for the drawings.
     */
    updateWorkingStyles: (state, { payload }: PayloadAction<DrawingStyles>) => {
      state.workingStyles = {
        ...state.workingStyles,
        ...payload,
      }
    },

    /**
     * Editor states
     */
    setLastModified: (state, { payload }: PayloadAction<number>) => {
      state.lastModified = payload
    },

    addEditingDrawingUuids: (state, { payload }: PayloadAction<string[]>) => {
      state.renderSkipUuids.push(...payload)
    },

    /**
     * Editor elements
     */
    // Given a set of IDs, toggle their checked state in the editor.
    // If `checked` is true, the IDs will be added to the editor elements.
    // If `checked` is false, the IDs will be removed from the editor elements.
    updateEditorElements: (
      state,
      { payload }: PayloadAction<{ editorElements: DrawingsState['document']['editorElements']; checked: boolean }>,
    ) => {
      const { editorElements, checked } = payload
      const { inspectionItems, polygons, cylinders } = state.document.editorElements
      if (checked) {
        // Add the elements to the editor
        state.document.editorElements.inspectionItems.push(...editorElements.inspectionItems)
        state.document.editorElements.polygons.push(...editorElements.polygons)
        state.document.editorElements.cylinders.push(...editorElements.cylinders)
      } else {
        // Remove the elements from the editor
        state.document.editorElements.inspectionItems = inspectionItems.filter(
          (item) => !editorElements.inspectionItems.some((el) => el.id === item.id),
        )
        state.document.editorElements.polygons = polygons.filter(
          (shape) => !editorElements.polygons.some((el) => el.id === shape.id),
        )
        state.document.editorElements.cylinders = cylinders.filter(
          (shape) => !editorElements.cylinders.some((el) => el.id === shape.id),
        )
      }

      state.lastModified = Date.now()
    },
    setEditorElements: (
      state,
      { payload: editorElements }: PayloadAction<DrawingsState['document']['editorElements']>,
    ) => {
      state.document.editorElements = editorElements
    },

    /**
     * Exports
     */
    set2dDrawings: (
      state,
      {
        payload: { drawings, mmToPxRatio, scale },
      }: PayloadAction<{ drawings: Drawing[]; mmToPxRatio: number; scale: number }>,
    ) => {
      state.document2d.drawings = drawings
      state.document2d.mmToPxRatio = mmToPxRatio
      state.document2d.scale = scale
      state.document2d.exportState = ExportState.Rendering
    },
    exportDrawings: (state, { payload }: PayloadAction<ExportState>) => {
      state.document2d.exportState = payload
    },
    resetExportDrawings: (state) => {
      state.document2d = initialState.document2d
    },
  },
})

export const {
  // Drawings management
  addDrawing,
  setDrawings,
  updateDrawingParameters,
  removeDrawings,
  resetDrawings,

  undo,

  updateDrawingStyles,
  updateWorkingStyles,
  updateDefaultStyles,

  // Editor elements
  updateEditorElements,
  setEditorElements,

  // Editor states
  setLastModified,
  addEditingDrawingUuids,

  // Export
  set2dDrawings,
  exportDrawings,
  resetExportDrawings,
} = drawingsSlice.actions

export default drawingsSlice.reducer
export const drawingsReducer = drawingsSlice.reducer
